// =============================================================================
// src/chrome/search-palette/search-palette-render.ts — markup dos resultados da palette.
//
// Proveniencia: porte FIEL de SearchPalette.render (3095-3155) e ._esc (3212-3216)
//   de assets/unified-app.js. O HTML gerado e byte-fiel: mesmas classes, mesma
//   ordem de atributos, mesmo escaping (& < > " '). NAO "limpar" o markup —
//   a paridade visual/CSS depende dele.
//
// SRP: este arquivo so produz strings HTML a partir dos itens. Sem DOM, sem estado.
// =============================================================================

import type { SearchPaletteItem } from './search-palette-items';
import { hintForAction } from './search-palette-items';

/** Item ja indexado com sua posicao global na lista filtrada (data-idx). */
interface IndexedPaletteItem {
  readonly item: SearchPaletteItem;
  readonly idx: number;
}

/** Grupo de itens preservando a ordem de aparicao (replica o agrupamento legado). */
interface PaletteGroup {
  readonly name: string;
  readonly items: IndexedPaletteItem[];
}

/**
 * Escapa HTML exatamente como SearchPalette._esc (& < > " '). Usado em todo texto
 * vindo de query/itens antes de ir para innerHTML.
 */
export function escapeHtml(raw: string): string {
  return String(raw)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Markup do estado vazio (replica o ramo `!items.length` de render). */
export function renderEmptyState(query: string): string {
  return `<div class="u-search-palette-empty">Nenhum resultado para <strong>${escapeHtml(query)}</strong></div>`;
}

/**
 * Markup completo dos resultados agrupados. `focusedIdx` marca o item focado
 * (aria-selected). Replica o `groups.map(...)` do render legado.
 */
export function renderResults(items: readonly SearchPaletteItem[], focusedIdx: number): string {
  const groups = groupByAppearance(items);
  return groups.map((group) => renderGroup(group, focusedIdx)).join('');
}

/** Agrupa itens por `group` preservando a ordem de aparicao (replica groupIdx legado). */
function groupByAppearance(items: readonly SearchPaletteItem[]): PaletteGroup[] {
  const groups: PaletteGroup[] = [];
  const indexByName = new Map<string, number>();
  items.forEach((item, idx) => {
    const at = indexByName.get(item.group);
    if (at === undefined) {
      indexByName.set(item.group, groups.length);
      groups.push({ name: item.group, items: [{ item, idx }] });
      return;
    }
    groups[at]?.items.push({ item, idx });
  });
  return groups;
}

/** Markup de um grupo: rotulo + linhas dos itens. */
function renderGroup(group: PaletteGroup, focusedIdx: number): string {
  const rows = group.items.map((entry) => renderRow(entry, focusedIdx)).join('');
  return `<div class="u-search-palette-group-label">${escapeHtml(group.name)}</div>${rows}`;
}

/** Markup de uma linha de resultado (icone/badge + texto + dica). */
function renderRow(entry: IndexedPaletteItem, focusedIdx: number): string {
  const { item, idx } = entry;
  const selectedAttr = idx === focusedIdx ? ' aria-selected="true"' : '';
  const hint = hintForAction(item.action);
  const hintHtml = hint ? `<span class="u-search-palette-item-hint">${hint}</span>` : '';
  return (
    `<div class="u-search-palette-item" role="option" data-idx="${idx}"${selectedAttr}>` +
    renderItemIcon(item) +
    renderItemText(item) +
    hintHtml +
    '</div>'
  );
}

/** Avatar do item: badge de partido (2 letras) ou icone Tabler (replica iconHtml legado). */
function renderItemIcon(item: SearchPaletteItem): string {
  if (item.party) {
    const sigla = escapeHtml(item.party.slice(0, 2).toUpperCase());
    return `<span class="u-search-palette-item-icon" data-party="${escapeHtml(item.party)}">${sigla}</span>`;
  }
  return `<span class="u-search-palette-item-icon"><i class="ti ${escapeHtml(item.icon || 'ti-circle')}"></i></span>`;
}

/** Bloco de texto do item: label + sub opcional. */
function renderItemText(item: SearchPaletteItem): string {
  const subHtml = item.sub
    ? `<span class="u-search-palette-item-sub">${escapeHtml(item.sub)}</span>`
    : '';
  return (
    '<div class="u-search-palette-item-text">' +
    `<span class="u-search-palette-item-label">${escapeHtml(item.label)}</span>` +
    subHtml +
    '</div>'
  );
}
